import React, { useEffect } from 'react';
import { useLocatorLov } from '../hooks/useLocatorLov';

interface EditableCellProps {
  column: string;
  value: string;
  lineId: string;
  line: any;
  onUpdate: (lineId: string, field: string, value: string) => void;
  isDarkTheme?: boolean;
  selectedHeaderToOrg?: string;
  subinventories: string[];
  refetchSubinventories?: (orgId: string) => void;
  refetchLocators?: (orgId: string, subinventory: string) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({
  column,
  value,
  lineId,
  line,
  onUpdate,
  isDarkTheme,
  selectedHeaderToOrg,
  subinventories,
  refetchSubinventories,
  refetchLocators
}) => {
  const { data: locators, refetch: refetchLocatorsHook } = useLocatorLov(
    selectedHeaderToOrg, 
    line.toSubinventory, 
    false
  );

  // Refresh locators when subinventory changes for locator column only
  useEffect(() => {
    if (selectedHeaderToOrg && line.toSubinventory && column === 'To Locator') {
      refetchLocatorsHook(selectedHeaderToOrg, line.toSubinventory);
    }
  }, [line.toSubinventory, column, selectedHeaderToOrg, refetchLocatorsHook]);




  const dropdownStyle = {
    width: '100%',
    padding: '0px',
    border: 'none',
    backgroundColor: 'transparent',
    color: 'inherit',
    fontSize: '12px',
    boxSizing: 'border-box' as const,
    outline: 'none'
  };

  const inputStyle = {
    width: '100%',
    padding: '4px',
    border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
    backgroundColor: isDarkTheme ? '#333' : '#fff',
    color: isDarkTheme ? '#fff' : '#000',
    fontSize: '12px',
    boxSizing: 'border-box' as const
  };

  if (column === 'To Subinventory') {
    // Check if API originally returned a value (not user-entered)
    // If line has returnLineId AND the original API response had toSubinventory value
    const isApiPreFilled = line.returnLineId && line.originalToSubinventory;
    if (isApiPreFilled) {
      return <div style={{ minHeight: '16px', padding: '4px' }}>{line.toSubinventory}</div>;
    }
    // Show dropdown if API returned NULL/empty (user can edit)
    return (
      <select
        value={value}
        onChange={(e) => {
          e.stopPropagation();
          const newSubinventory = e.target.value;
          console.log('Subinventory onChange:', newSubinventory, 'for line:', lineId);
          
          // Update immediately without waiting
          onUpdate(lineId, column, newSubinventory);
          
          // Use setTimeout to ensure state updates complete before other operations
          setTimeout(() => {
            // Clear locator when subinventory changes
            if (line.toLocator) {
              onUpdate(lineId, 'To Locator', '');
            }
            // Fetch locators for new subinventory
            if (selectedHeaderToOrg && newSubinventory) {
              refetchLocatorsHook(selectedHeaderToOrg, newSubinventory, (newLocators) => {
                console.log('New locators received:', newLocators);
              });
            }
          }, 0);
        }}
        onBlur={(e) => {
          e.stopPropagation();
          onUpdate(lineId, column, e.target.value);
        }}
        onKeyDown={(e) => {
          // Allow keyboard navigation for select dropdown
          if (['ArrowUp', 'ArrowDown', 'Enter', 'Space'].includes(e.key)) {
            e.stopPropagation();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        onFocus={() => {
          if (selectedHeaderToOrg && subinventories.length === 0 && refetchSubinventories) {
            refetchSubinventories(selectedHeaderToOrg);
          }
        }}
        style={dropdownStyle}
      >
        <option value="">Select subinventory</option>
        {/* Add current value if not in subinventories list (for scanned values) */}
        {value && !subinventories.includes(value) && (
          <option key={value} value={value}>
            {value}
          </option>
        )}
        {subinventories.map((subinventory) => (
          <option key={subinventory} value={subinventory}>
            {subinventory}
          </option>
        ))}
      </select>
    );
  }

  if (column === 'To Locator') {

    // Check if API originally returned a value (not user-entered)
    // If line has returnLineId AND the original API response had toLocator value
    const isApiPreFilled = line.returnLineId && line.originalToLocator;
    if (isApiPreFilled) {
      return <div style={{ minHeight: '16px', padding: '4px' }}>{line.toLocator}</div>;
    }
    // Show dropdown if API returned NULL/empty (user can edit)
    return (
      <select
        value={value}
        onChange={(e) => onUpdate(lineId, column, e.target.value)}
        onBlur={(e) => onUpdate(lineId, column, e.target.value)}
        onKeyDown={(e) => {
          // Allow keyboard navigation for select dropdown
          if (['ArrowUp', 'ArrowDown', 'Enter', 'Space'].includes(e.key)) {
            e.stopPropagation();
          }
        }}
        onFocus={() => {
          if (selectedHeaderToOrg && line.toSubinventory && locators.length === 0) {
            refetchLocatorsHook(selectedHeaderToOrg, line.toSubinventory);
          }
        }}

        disabled={!line.toSubinventory}
        style={dropdownStyle}
      >
        <option value="">Select locator</option>
        {locators.map((locator) => (
          <option key={locator} value={locator}>
            {locator}
          </option>
        ))}
      </select>
    );
  }

  if (column === 'Repair Eligibility') {
    return (
      <select
        value={value}
        onChange={(e) => {
          e.stopPropagation();
          onUpdate(lineId, column, e.target.value);
        }}
        onBlur={(e) => {
          e.stopPropagation();
          onUpdate(lineId, column, e.target.value);
        }}
        onKeyDown={(e) => {
          // Allow keyboard navigation for select dropdown
          if (['ArrowUp', 'ArrowDown', 'Enter', 'Space'].includes(e.key)) {
            e.stopPropagation();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        style={dropdownStyle}
      >
        <option value="">Select...</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    );
  }

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onUpdate(lineId, column, e.target.value)}
      onBlur={(e) => onUpdate(lineId, column, e.target.value)}
      placeholder={`Enter ${column.toLowerCase()}`}
      style={inputStyle}
    />
  );
};

export default EditableCell;